import * as React from 'react';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import {
    ApiOutlined,
    ArrowRightOutlined,
    BranchesOutlined,
    PlusOutlined,
    RightOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectManageableWorkflowSteps,
    selectAppIntegrations,
    selectDestinationInfoByDestinationStepConfigId,
    selectAppPlatform,
    selectAppStoreType,
    selectIsWorkflowsCIStepEnabled,
    selectStepGraph,
    selectWorkflowId,
} from '@/selectors/selectors';
import {
    type IntegrationCapability,
    type IntegrationInstallationId,
    ReleaseStepType,
    RunwayResponseType,
} from '@/types';
import { displayStringForDestination, displayTextForReleaseStepType } from '@/helpers/displayHelpers';
import { displayNameForCapability } from '@/helpers';
import { antGrey, titleStyle } from '../styles/globalStyles';
import { integrationSelectOptions } from '@/helpers/integrationSelectHelpers';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import { WorkflowUpdatable } from '@/actions/workflow';
import type { StepGraphStep } from '@/types/workflow';
import { globalMessage } from '@/utils/globalMessage';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';
import { useMdmPlatformOverride } from '@/helpers/mdmPlatformOverride';

enum WizardPhase {
    Settings = 'Settings',
    Integrations = 'Integrations',
    UpstreamCI = 'UpstreamCI',
}

const WIZARD_PHASE_LABELS: Record<WizardPhase, { label: string; icon: React.ReactNode }> = {
    [WizardPhase.Settings]: { label: 'General settings', icon: <SettingOutlined style={{ fontSize: 14 }} /> },
    [WizardPhase.Integrations]: { label: 'Integration settings', icon: <ApiOutlined style={{ fontSize: 14 }} /> },
    [WizardPhase.UpstreamCI]: { label: 'CI builds', icon: <BranchesOutlined style={{ fontSize: 14 }} /> },
};

const CI_BUILD_USAGE_TOOLTIP_BY_ARCHETYPE: Partial<Record<ReleaseStepType, string>> = {
    [ReleaseStepType.BetaTesting]: 'CI build that will be distributed to your beta testers.',
    [ReleaseStepType.Takeoff]: 'CI build that will be submitted to the app store and released.',
    [ReleaseStepType.RegressionTesting]:
        'CI build that will be tested before the release can move on. Leave empty if this step does not use CI builds.',
};

// Stands in for the step being created, which has no id to select yet.
const NEW_CI_STEP = 'newCIStep';

const WizardPhaseLabel: React.FC<{ isActive: boolean; icon: React.ReactNode; label: string }> = ({
    isActive,
    icon,
    label,
}) => (
    <Space size={12} style={{ color: isActive ? antGrey.grey9 : antGrey.grey7 }}>
        {icon}
        <Text style={{ color: 'inherit' }}>{label}</Text>
    </Space>
);

interface AddStepModalProps {
    appId: string;
    orgId: string;
    isOpen: boolean;
    onClose: () => void;
    stepGroupIndex: number;
    archetypeGroupIndex: number;
    insertAsNewArchetypeGroup: boolean;
    // Fixed for the row-level button, which only adds another step of its group's archetype.
    lockedArchetype?: ReleaseStepType;
}

export const AddStepModal: React.FC<AddStepModalProps> = ({
    appId,
    orgId,
    isOpen,
    onClose,
    stepGroupIndex,
    archetypeGroupIndex,
    insertAsNewArchetypeGroup,
    lockedArchetype,
}) => {
    const manageableWorkflowSteps = useSelector((state: RootState) => selectManageableWorkflowSteps(state, appId));
    const [phase, setPhase] = React.useState<WizardPhase>(WizardPhase.Settings);
    const [archetype, setArchetype] = React.useState<ReleaseStepType | undefined>(lockedArchetype);
    const [name, setName] = React.useState('');
    const [placeholder, setPlaceholder] = React.useState('');
    const [installationIdsByCapability, setInstallationIdsByCapability] = React.useState<
        Partial<Record<IntegrationCapability, IntegrationInstallationId>>
    >({});
    const [upstreamCIStepConfigId, setUpstreamCIStepConfigId] = React.useState<string | undefined>(undefined);
    const [destinationStepIds, setDestinationStepIds] = React.useState<string[]>([]);
    const [ciChoiceByDownstreamStepConfigId, setCiChoiceByDownstreamStepConfigId] = React.useState<
        Record<string, string>
    >({});
    const [hasTriedSubmitting, setHasTriedSubmitting] = React.useState(false);

    const mdmOverride = useMdmPlatformOverride(appId);
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const integrations = useSelector((state: RootState) => selectAppIntegrations(state, appId));
    const stepGraph = useSelector((state: RootState) => selectStepGraph(state, appId));
    const destinationInfoByDestinationStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );

    const [createStepConfig, isCreating, createResponseType, createError] = useCallUpdatable(
        WorkflowUpdatable.CreateStepConfig,
        { orgId, workflowId: workflowId ?? '' },
    );

    const destinationOptions = React.useMemo(
        () => Object.values(destinationInfoByDestinationStepConfigId),
        [destinationInfoByDestinationStepConfigId],
    );

    // Every destination is a candidate until one is picked, so the wizard settles its phases up front.
    const consideredDestinationStepIds = React.useMemo(
        () =>
            destinationStepIds.length > 0 ?
                destinationStepIds
            :   destinationOptions.map((destination) => destination.stepConfigId),
        [destinationStepIds, destinationOptions],
    );

    const stepsOnDestinations = React.useMemo(() => {
        const steps: StepGraphStep[] = [];

        for (const stepGroup of stepGraph?.stepGroups ?? []) {
            for (const group of stepGroup.archetypeGroups) {
                for (const step of group.steps) {
                    const servesAPickedDestination = step.destinationStepConfigIds.some((destinationStepConfigId) =>
                        consideredDestinationStepIds.includes(destinationStepConfigId),
                    );
                    if (servesAPickedDestination) {
                        steps.push(step);
                    }
                }
            }
        }

        return steps;
    }, [stepGraph, consideredDestinationStepIds]);

    const ciStepsOnDestinations = React.useMemo(
        () => stepsOnDestinations.filter((step) => step.archetype === ReleaseStepType.ReleaseCandidate),
        [stepsOnDestinations],
    );
    const downstreamStepsOnDestinations = stepsOnDestinations.filter(
        (step) =>
            step.supportsUpstreamCIStep &&
            step.destinationStepConfigIds.every((destinationStepConfigId) =>
                consideredDestinationStepIds.includes(destinationStepConfigId),
            ),
    );

    const downstreamCIStepChoices = downstreamStepsOnDestinations.map((step) => {
        // One explicit upstream is used for every destination this step serves, so the CI has to serve all of them too.
        const selectableCISteps = ciStepsOnDestinations.filter((ciStep) =>
            step.destinationStepConfigIds.every((destinationStepConfigId) =>
                ciStep.destinationStepConfigIds.includes(destinationStepConfigId),
            ),
        );
        const explicitCIStep = selectableCISteps.find((ciStep) => ciStep.stepConfigId === step.upstreamCIStepConfigId);
        const currentCIStep = explicitCIStep ?? (selectableCISteps.length === 1 ? selectableCISteps[0] : undefined);
        const newStepWillServeAllDestinations = step.destinationStepConfigIds.every((destinationStepConfigId) =>
            destinationStepIds.includes(destinationStepConfigId),
        );

        // Regression testing can use a strategy that never looks at a build, so it's the only one truly optional.
        const isRequired = step.archetype !== ReleaseStepType.RegressionTesting;

        return {
            step,
            selectableCISteps,
            currentCIStep,
            isRequired,
            newStepWillServeAllDestinations,
            selectedCIStepConfigId: ciChoiceByDownstreamStepConfigId[step.stepConfigId] ?? currentCIStep?.stepConfigId,
        };
    });

    const selectedArchetype = manageableWorkflowSteps.find((option) => option.archetype === archetype);
    const requiredCapabilities = React.useMemo(
        () => selectedArchetype?.requiredCapabilities ?? [],
        [selectedArchetype],
    );
    const optionalCapabilities = React.useMemo(
        () => selectedArchetype?.optionalCapabilities ?? [],
        [selectedArchetype],
    );
    const supportsUpstreamCIStep = selectedArchetype?.supportsUpstreamCIStep ?? false;
    const supportsMultipleDestinations = selectedArchetype?.supportsMultipleDestinations ?? false;
    const soleDestinationStepId = destinationOptions.length === 1 ? destinationOptions[0].stepConfigId : undefined;

    const installationsForCapability = React.useCallback(
        (capability: IntegrationCapability) =>
            integrations.filter((integration) => integration.capabilities?.includes(capability)),
        [integrations],
    );

    // Keyed on the archetype rather than the list, whose identity changes whenever app settings are refetched.
    const defaultArchetype = manageableWorkflowSteps[0]?.archetype;

    // Opening the modal starts a fresh step, so nothing from a previous one carries over.
    React.useEffect(() => {
        if (!isOpen) return;

        setPhase(WizardPhase.Settings);
        setArchetype(lockedArchetype ?? defaultArchetype);
        setCiChoiceByDownstreamStepConfigId({});
        setHasTriedSubmitting(false);
    }, [isOpen, lockedArchetype, defaultArchetype]);

    // Keyed on the id rather than the list, whose identity changes on every render. The step type is
    // in here too, since it decides how many destinations may be picked.
    React.useEffect(() => {
        if (!isOpen) return;

        setDestinationStepIds(soleDestinationStepId ? [soleDestinationStepId] : []);
    }, [isOpen, soleDestinationStepId, archetype]);

    // Name and integrations follow the chosen step type, which can change while the modal is open.
    React.useEffect(() => {
        if (!isOpen || !archetype) return;

        const stepDisplayName = displayTextForReleaseStepType(
            archetype,
            platform,
            appStoreType ?? [],
            isWorkflowsCIStepEnabled,
        );
        setPlaceholder(stepDisplayName ?? '');
        setUpstreamCIStepConfigId(
            ciStepsOnDestinations.length === 1 ? ciStepsOnDestinations[0].stepConfigId : undefined,
        );

        const autoSelected: Partial<Record<IntegrationCapability, IntegrationInstallationId>> = {};
        [...requiredCapabilities, ...optionalCapabilities].forEach((capability) => {
            const candidates = installationsForCapability(capability);
            if (candidates.length === 1) {
                autoSelected[capability] = candidates[0].installationId;
            }
        });
        setInstallationIdsByCapability(autoSelected);
    }, [
        isOpen,
        archetype,
        platform,
        appStoreType,
        isWorkflowsCIStepEnabled,
        ciStepsOnDestinations,
        requiredCapabilities,
        optionalCapabilities,
        installationsForCapability,
    ]);

    // A ref, since onClose identity changes each render and the response type stays Success after the first pass.
    const didHandleCreateSuccessRef = React.useRef(false);
    React.useEffect(() => {
        const didSucceed = createResponseType === RunwayResponseType.Success;
        if (didSucceed && !didHandleCreateSuccessRef.current) {
            didHandleCreateSuccessRef.current = true;
            globalMessage.success('Step added');
            onClose();
        }
        if (!didSucceed) {
            didHandleCreateSuccessRef.current = false;
        }
    }, [createResponseType, onClose]);

    const needsUpstreamCIReview = archetype === ReleaseStepType.ReleaseCandidate && downstreamCIStepChoices.length > 0;

    const hasIntegrationPhase = requiredCapabilities.length > 0 || optionalCapabilities.length > 0;
    const phases = [
        WizardPhase.Settings,
        ...(hasIntegrationPhase ? [WizardPhase.Integrations] : []),
        ...(needsUpstreamCIReview ? [WizardPhase.UpstreamCI] : []),
    ];
    const phaseIndex = Math.max(phases.indexOf(phase), 0);
    const missingRequiredCapabilities = requiredCapabilities.filter(
        (capability) => !installationIdsByCapability[capability],
    );
    const hasMissingRequiredCIChoice = downstreamCIStepChoices.some(
        (choice) => choice.isRequired && !choice.selectedCIStepConfigId,
    );
    const areDetailsValid = !!archetype && name.trim().length > 0 && destinationStepIds.length > 0;
    const canSubmit = areDetailsValid && missingRequiredCapabilities.length === 0;
    const canAdvance =
        areDetailsValid && (phase !== WizardPhase.Integrations || missingRequiredCapabilities.length === 0);

    // Adding another CI makes an implicit single-CI fallback ambiguous, so preserve the CI shown by the wizard.
    const ciChoicesToSend: Record<string, string> = {};
    downstreamCIStepChoices.forEach(({ step, currentCIStep, isRequired, selectedCIStepConfigId }) => {
        const changedChoice = ciChoiceByDownstreamStepConfigId[step.stepConfigId];
        if (!selectedCIStepConfigId) return;

        const shouldPreserveImplicitChoice =
            !step.upstreamCIStepConfigId &&
            !!currentCIStep &&
            step.destinationStepConfigIds.some((destinationStepConfigId) =>
                destinationStepIds.includes(destinationStepConfigId),
            );
        if (
            !isRequired &&
            !shouldPreserveImplicitChoice &&
            (!changedChoice || changedChoice === step.upstreamCIStepConfigId)
        ) {
            return;
        }

        ciChoicesToSend[step.stepConfigId] = selectedCIStepConfigId === NEW_CI_STEP ? '' : selectedCIStepConfigId;
    });

    const onSubmit = () => {
        if (!archetype) return;
        createStepConfig({
            archetype,
            name: name.trim(),
            integrationInstallationIds: Object.values(installationIdsByCapability).filter(
                (installationId): installationId is IntegrationInstallationId => Boolean(installationId),
            ),
            stepGroupIndex,
            archetypeGroupIndex,
            insertAsNewArchetypeGroup,
            destinationStepConfigIds: destinationStepIds,
            archetypeSpecificSettings:
                archetype === ReleaseStepType.ReleaseCandidate ?
                    { ciStepConfigIdByDownstreamStepConfigId: ciChoicesToSend }
                : supportsUpstreamCIStep ? { upstreamCIStepConfigId }
                : undefined,
        });
    };

    const isLastPhase = phaseIndex === phases.length - 1;
    const handlePrimaryAction = () => {
        if (isLastPhase) {
            if (!canSubmit || isCreating) return;

            if (hasMissingRequiredCIChoice) {
                setHasTriedSubmitting(true);
                return;
            }

            onSubmit();
            return;
        }

        if (canAdvance) {
            setPhase(phases[phaseIndex + 1]);
        }
    };

    const submitButton = (
        <Button
            key="submit"
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canSubmit}
            loading={isCreating}
            onClick={handlePrimaryAction}
        >
            Add step
        </Button>
    );

    const footer = [
        phaseIndex === 0 ?
            <Button key="cancel" onClick={onClose}>
                Cancel
            </Button>
        :   <Button key="back" onClick={() => setPhase(phases[phaseIndex - 1])}>
                Back
            </Button>,
        isLastPhase ? submitButton : (
            <Button
                key="next"
                type="primary"
                icon={<ArrowRightOutlined />}
                iconPosition="end"
                disabled={!canAdvance}
                onClick={handlePrimaryAction}
            >
                Next
            </Button>
        ),
    ];

    const renderCapabilitySection = (title: string, capabilities: IntegrationCapability[], isRequired: boolean) => {
        if (capabilities.length === 0) return null;

        return (
            <>
                <Text style={{ ...titleStyle, fontWeight: 400, fontSize: 14, marginBottom: 16, display: 'block' }}>
                    {title}
                </Text>

                {capabilities.map((capability) => {
                    const candidates = installationsForCapability(capability);
                    return (
                        <Form.Item key={capability} label={displayNameForCapability(capability)}>
                            <Select
                                value={installationIdsByCapability[capability]}
                                onChange={(installationId: IntegrationInstallationId | undefined) =>
                                    setInstallationIdsByCapability((current) => ({
                                        ...current,
                                        [capability]: installationId,
                                    }))
                                }
                                placeholder={
                                    candidates.length === 0 ?
                                        `No ${displayNameForCapability(capability)} integration connected`
                                    :   'Select an integration'
                                }
                                disabled={candidates.length === 0}
                                options={integrationSelectOptions(candidates)}
                                optionFilterProp="title"
                                allowClear={!isRequired}
                            />
                        </Form.Item>
                    );
                })}
            </>
        );
    };

    return (
        <Modal open={isOpen} width={600} title="Add step" onCancel={onClose} footer={footer} destroyOnClose>
            {phases.length > 1 && (
                <Space size={0} style={{ marginBottom: 24 }}>
                    {phases.map((wizardPhase, index) => (
                        <React.Fragment key={wizardPhase}>
                            {index > 0 && (
                                <RightOutlined style={{ fontSize: 15, color: antGrey.grey5, padding: '0 20px' }} />
                            )}
                            <WizardPhaseLabel
                                isActive={phase === wizardPhase}
                                icon={WIZARD_PHASE_LABELS[wizardPhase].icon}
                                label={WIZARD_PHASE_LABELS[wizardPhase].label}
                            />
                        </React.Fragment>
                    ))}
                </Space>
            )}

            <Form layout="vertical">
                {phase === WizardPhase.Settings ?
                    <>
                        <Form.Item label="Step type">
                            <Select
                                value={archetype}
                                disabled={!!lockedArchetype}
                                onChange={setArchetype}
                                options={manageableWorkflowSteps.map((option) => ({
                                    value: option.archetype,
                                    label: displayTextForReleaseStepType(
                                        option.archetype,
                                        platform,
                                        appStoreType ?? [],
                                        isWorkflowsCIStepEnabled,
                                    ),
                                }))}
                            />
                        </Form.Item>

                        <Form.Item label="Name">
                            <Input
                                required
                                placeholder={placeholder}
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                onPressEnter={handlePrimaryAction}
                                maxLength={80}
                            />
                        </Form.Item>

                        {destinationOptions.length > 1 && (
                            <Form.Item label={supportsMultipleDestinations ? 'Destinations' : 'Destination'}>
                                <Select
                                    mode={supportsMultipleDestinations ? 'multiple' : undefined}
                                    value={supportsMultipleDestinations ? destinationStepIds : destinationStepIds[0]}
                                    onChange={(stepConfigIds: string | string[]) => {
                                        setDestinationStepIds(
                                            typeof stepConfigIds === 'string' ? [stepConfigIds] : stepConfigIds,
                                        );
                                        setCiChoiceByDownstreamStepConfigId({});
                                    }}
                                    placeholder={
                                        supportsMultipleDestinations ? 'Select destinations' : 'Select a destination'
                                    }
                                    options={destinationOptions.map((destination) => ({
                                        value: destination.stepConfigId,
                                        label: displayStringForDestination(
                                            destination.platform,
                                            destination.appStoreType,
                                            destination.name,
                                            destination.appName,
                                            mdmOverride?.label,
                                        ),
                                    }))}
                                />
                            </Form.Item>
                        )}

                        {supportsUpstreamCIStep && (
                            <Form.Item
                                label="Upstream CI step"
                                extra="Step that will produce the builds this step uses. Leave empty if this step does not use CI builds."
                            >
                                <Select
                                    value={upstreamCIStepConfigId}
                                    onChange={setUpstreamCIStepConfigId}
                                    allowClear
                                    placeholder="Select a CI step"
                                    options={ciStepsOnDestinations.map((step) => ({
                                        value: step.stepConfigId,
                                        label: step.stepName,
                                    }))}
                                />
                            </Form.Item>
                        )}

                        <Alert
                            showIcon
                            type="warning"
                            message="This step will be added to all active releases and all future releases."
                        />
                    </>
                : phase === WizardPhase.UpstreamCI ?
                    <>
                        <Text style={{ display: 'block', marginBottom: 24 }}>
                            Confirm which CI steps will produce the builds used by the steps below.
                        </Text>

                        {downstreamCIStepChoices.map(
                            ({
                                step,
                                isRequired,
                                selectableCISteps,
                                currentCIStep,
                                newStepWillServeAllDestinations,
                                selectedCIStepConfigId,
                            }) => {
                                const isMissing = hasTriedSubmitting && isRequired && !selectedCIStepConfigId;

                                return (
                                    <Form.Item
                                        key={step.stepConfigId}
                                        label={
                                            <Space size={8}>
                                                {step.stepName}
                                                <TooltipInfoIcon
                                                    tooltipContent={
                                                        CI_BUILD_USAGE_TOOLTIP_BY_ARCHETYPE[step.archetype] ??
                                                        'CI build that this step will use.'
                                                    }
                                                />
                                            </Space>
                                        }
                                        validateStatus={isMissing ? 'error' : undefined}
                                        help={
                                            isMissing ? 'Select the CI step that will produce this build.' : undefined
                                        }
                                    >
                                        <Select
                                            value={selectedCIStepConfigId}
                                            allowClear
                                            placeholder="Not set (current)"
                                            onChange={(choice: string) =>
                                                setCiChoiceByDownstreamStepConfigId((current) => ({
                                                    ...current,
                                                    [step.stepConfigId]: choice,
                                                }))
                                            }
                                            options={[
                                                ...(currentCIStep ?
                                                    [
                                                        {
                                                            value: currentCIStep.stepConfigId,
                                                            label: `${currentCIStep.stepName} (current)`,
                                                        },
                                                    ]
                                                :   []),
                                                ...selectableCISteps
                                                    .filter(
                                                        (ciStep) => ciStep.stepConfigId !== currentCIStep?.stepConfigId,
                                                    )
                                                    .map((ciStep) => ({
                                                        value: ciStep.stepConfigId,
                                                        label: ciStep.stepName,
                                                    })),
                                                ...(newStepWillServeAllDestinations ?
                                                    [
                                                        {
                                                            value: NEW_CI_STEP,
                                                            label: `${name.trim() || placeholder} (new)`,
                                                        },
                                                    ]
                                                :   []),
                                            ]}
                                        />
                                    </Form.Item>
                                );
                            },
                        )}
                    </>
                :   <>
                        {renderCapabilitySection('Required integrations', requiredCapabilities, true)}
                        {renderCapabilitySection('Optional integrations', optionalCapabilities, false)}
                    </>
                }

                {createError && <Alert type="error" showIcon message={createError} />}
            </Form>
        </Modal>
    );
};
